import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { _error, _log, _warn } from '@shared/aux_helper_environment';
import { _cloneDeep, _formatChartsDate } from '@shared/aux_helper_functions';
import { IanTranslateService } from 'core/services/ian-core-singleton.service';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexTheme,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ChartComponent,
} from 'ng-apexcharts';
import { GenieStatementResponse } from 'app/services/agents/databricks-genie-ai-chat.models';
import {
  DataBricksAiGenie_AnalyzeMultiSeriesChartData,
  DataBricksAiGenie_AnalyzeSimpleChartData,
} from './_aux_dataBricksAiGenieQueryProcessTable';
import {
  DataBricksAiGenie_ChartAnalysisResult,
  DataBricksAiGenie_ChartAnalysisResult_ChartType,
} from './databricks-genie-ai-query-process-table.component';

type ApexChartType = 'bar' | 'pie' | 'line';

export interface ChartOptions {
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  theme: ApexTheme;
  title: ApexTitleSubtitle;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  labels: string[];
  colors: any;
  stroke?: {
    curve?: string;
    width?: number;
  };
  markers?: {
    size: number;
    hover: {
      size: number;
    };
  };
}

interface ChartDialogData {
  chartData: DataBricksAiGenie_ChartAnalysisResult['visualization'];
  isDialogMode: boolean;
  // Para edición interactiva en el chart (controles de tipo / etiqueta / series):
  queryProcessResult?: GenieStatementResponse;
  availableNumericColumns?: string[];
  availableLabelColumns?: string[];
}

/** Tipos de gráfico que el usuario puede elegir manualmente. */
type SelectableChartType = DataBricksAiGenie_ChartAnalysisResult_ChartType;

/** Tipos que soportan varias series Y simultáneamente. */
const MULTI_SERIES_CHART_TYPES: ReadonlySet<SelectableChartType> = new Set<SelectableChartType>(['bar', 'horizontalBar', 'time_line']);

/** Modo de eje en multi-serie con escalas distintas. */
type AxisMode = 'auto' | 'shared' | 'dual';

const chartConfig: Omit<ChartOptions, 'series'> = {
  chart: {
    height: 500,
    type: 'bar' as ApexChartType,
    toolbar: {
      show: true,
      tools: {
        download: true,
        selection: false,
        zoom: false,
        zoomin: false,
        zoomout: false,
        pan: false,
        reset: false,
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: '70%',
    },
    pie: {
      donut: {
        size: '65%',
      },
    },
  },
  xaxis: {
    type: 'category',
    labels: {
      rotate: -45,
      trim: true,
      maxHeight: 100,
    },
  },
  yaxis: {
    labels: {
      formatter: val => (val != null ? val.toLocaleString() : ''),
    },
  },
  theme: {
    mode: 'light',
  },
  title: {
    text: '',
    align: 'center',
  },
  tooltip: {
    y: {
      formatter: val => (val != null ? val.toLocaleString() : ''),
    },
  },
  legend: {
    show: true,
    position: 'right',
    fontSize: '14px',
  },
  labels: [],
  colors: ['#007bee', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#546E7A', '#26a69a', '#D10CE8'],
};

@Component({
  selector: 'query-process-chart',
  template: `
    <div *ngIf="isDialogMode" class="dialog-header">
      <h1 mat-dialog-title>{{ getChartTitle() }}</h1>
      <div class="dialog-actions">
        <mat-slide-toggle
          *ngIf="showRegistrosToggle"
          [(ngModel)]="showRegistrosData"
          (change)="onToggleChange()"
          class="registros-toggle"
          [matTooltip]="getShowGroupedValuesTooltip()"
        >
          {{ getShowGroupedValuesText() }}
        </mat-slide-toggle>
        <button mat-icon-button (click)="closeDialog()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- Controles compactos: tipo / etiqueta / series / modo de eje -->
    <div *ngIf="isDialogMode && isEditableMode" class="chart-controls">
      <mat-form-field appearance="outline" class="control-field">
        <mat-label>{{ getTypeLabel() }}</mat-label>
        <mat-select [(ngModel)]="selectedChartType" (selectionChange)="onChartTypeChange()">
          <mat-option *ngFor="let t of availableChartTypes" [value]="t">{{ getTypeOptionLabel(t) }}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field *ngIf="availableLabelOptions.length >= 1" appearance="outline" class="control-field">
        <mat-label>{{ getLabelAxisLabel() }}</mat-label>
        <mat-select [(ngModel)]="selectedLabelColumn" (selectionChange)="onLabelChange()">
          <mat-option *ngFor="let c of availableLabelOptions" [value]="c">{{ formatColumnTitle(c) }}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field *ngIf="availableNumericOptions.length > 1" appearance="outline" class="control-field">
        <mat-label>{{ getSeriesLabel() }}</mat-label>
        <mat-select [(ngModel)]="selectedSeries" (selectionChange)="onSeriesChange()" [multiple]="isMultiSeriesAllowed">
          <mat-option *ngFor="let c of availableNumericOptions" [value]="c">{{ formatColumnTitle(c) }}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field *ngIf="isMultiSeriesAllowed && selectedSeries.length >= 2" appearance="outline" class="control-field">
        <mat-label>{{ getAxisModeLabel() }}</mat-label>
        <mat-select [(ngModel)]="axisMode" (selectionChange)="onAxisModeChange()">
          <mat-option value="auto">{{ getAxisModeOptionLabel('auto') }}</mat-option>
          <mat-option value="shared">{{ getAxisModeOptionLabel('shared') }}</mat-option>
          <mat-option value="dual">{{ getAxisModeOptionLabel('dual') }}</mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <div class="chart-container">
      <div *ngIf="!isUpdating && chartData?.series?.length > 0 && chartConfig?.chart">
        <apx-chart
          #chart
          [series]="chartData.series"
          [chart]="chartConfig.chart"
          [dataLabels]="chartConfig.dataLabels"
          [plotOptions]="chartConfig.plotOptions"
          [xaxis]="chartConfig.xaxis"
          [yaxis]="chartConfig.yaxis"
          [theme]="chartConfig.theme"
          [tooltip]="chartConfig.tooltip"
          [labels]="chartConfig.labels"
          [stroke]="chartConfig.stroke"
          [markers]="chartConfig.markers"
          [colors]="chartConfig.colors"
          [legend]="chartConfig.legend"
        ></apx-chart>
      </div>
    </div>
  `,
  styles: [
    `
      .dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 16px;
        background: #fafafa;
      }

      .dialog-header h1 {
        margin: 0;
        flex: 1;
      }

      .dialog-actions {
        display: flex;
        align-items: center;
        gap: 16px;
      }

      .registros-toggle {
        margin: 0;
      }

      .chart-controls {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        padding: 4px 16px 0 16px;
        background: #fafafa;
      }

      .chart-controls .control-field {
        min-width: 160px;
        flex: 0 1 220px;
      }

      :host ::ng-deep .chart-controls .mat-form-field-wrapper {
        padding-bottom: 0;
      }

      :host ::ng-deep .chart-controls .mat-form-field-infix {
        padding: 6px 0;
        font-size: 13px;
      }

      .chart-container {
        padding: 16px;
        /* La altura efectiva del chart la fija ApexCharts (calculateChartHeight) en px.
           Dejar height: auto para que el contenedor no recorte cuando hay control bar. */
        overflow: auto;
        overflow-x: hidden;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataBricksAiGenieQueryProcessChartComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chart') chart: ChartComponent;

  @Input() chartMetadata: DataBricksAiGenie_ChartAnalysisResult;
  isDialogMode = false;

  chartData: { series: ApexAxisChartSeries | ApexNonAxisChartSeries } = {
    series: [],
  };

  chartConfig = _cloneDeep(chartConfig);

  showRegistrosToggle = false;
  showRegistrosData = true;
  originalSeries: any[] = [];
  registrosColumnIndex = -1;
  isUpdating = false;

  acummRegisterColumnPartialString = 'registros.)';

  // ──────────────────────────────────────────────────────────────────────────
  // Estado de los controles in-chart (tipo / etiqueta / series / modo de eje)
  // ──────────────────────────────────────────────────────────────────────────
  isEditableMode = false;
  availableChartTypes: SelectableChartType[] = ['bar', 'horizontalBar', 'time_line', 'pie'];
  availableLabelOptions: string[] = [];
  availableNumericOptions: string[] = [];

  selectedChartType: SelectableChartType = 'bar';
  selectedLabelColumn = '';
  selectedSeries: string[] = [];
  axisMode: AxisMode = 'auto';

  get isMultiSeriesAllowed(): boolean {
    return MULTI_SERIES_CHART_TYPES.has(this.selectedChartType);
  }

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ChartDialogData,
    @Optional() private dialogRef: MatDialogRef<DataBricksAiGenieQueryProcessChartComponent>,
    private cdRef: ChangeDetectorRef,
    private translate: IanTranslateService
  ) {
    this.isDialogMode = data?.isDialogMode === true;

    // Habilitar controles in-chart solo si tenemos el queryResult original (para re-analizar)
    // y al menos algo que elegir (más de 1 numérica o más de 1 label).
    const numCount = data?.availableNumericColumns?.length || 0;
    const labelCount = data?.availableLabelColumns?.length || 0;
    this.isEditableMode = !!(data?.queryProcessResult && numCount + labelCount > 0);

    if (this.isEditableMode) {
      this.availableNumericOptions = data.availableNumericColumns || [];
      this.availableLabelOptions = data.availableLabelColumns || [];

      // Pre-seleccionar desde el chartData entrante
      this.selectedChartType = data.chartData?.chartType || 'bar';
      this.selectedLabelColumn = data.chartData?.labelColumnName || this.availableLabelOptions[0] || '';
      this.selectedSeries = data.chartData?.numericColumnNames?.length
        ? [...data.chartData.numericColumnNames]
        : data.chartData?.numericColumnName
        ? [data.chartData.numericColumnName]
        : this.availableNumericOptions.slice(0, 1);

      // Modo de eje: si vino con dual axis del analyzer, partir en 'dual'; sino 'auto'
      this.axisMode = data.chartData?.useDualAxis ? 'dual' : 'auto';
    }
  }

  get localStringFormat(): string {
    return navigator.language || navigator.languages[0] || 'default';
  }

  /** Marca cuando el componente fue destruido — evita callbacks asíncronos post-cierre. */
  private destroyed = false;
  /** Handler de resize guardado para poder removerlo en ngOnDestroy (evita leak). */
  private resizeHandler: (() => void) | null = null;

  ngOnInit() {
    setTimeout(() => {
      if (this.destroyed) return;
      try {
        this.initializeChart();
      } catch (e) {
        _error('[ChartComponent] ngOnInit / initializeChart fail', e);
      }
      if (!this.destroyed) this.cdRef.detectChanges();
    }, 256);
  }

  ngAfterViewInit() {
    this.resizeHandler = () => {
      if (this.destroyed) return;
      try {
        const newHeight = this.calculateChartHeight();
        if (this.chart?.chart) {
          (this.chart.chart as any).updateOptions({ chart: { height: newHeight } });
        }
      } catch (e) {
        _warn('[ChartComponent] resize fail', e);
      }
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
  }

  getChartTitle(): string {
    const chartData = this.data?.chartData;
    const labelName = chartData?.labelColumnName || '';
    const multiNames = chartData?.numericColumnNames;
    const numericName = chartData?.numericColumnName || '';

    const titleNumeric =
      multiNames && multiNames.length > 1
        ? multiNames.map(n => DataBricksAiGenieQueryProcessChartComponent.formatColumnTitle(n)).join(' vs ')
        : DataBricksAiGenieQueryProcessChartComponent.formatColumnTitle(numericName);

    return `Gráfico:
    ${titleNumeric} /
    ${DataBricksAiGenieQueryProcessChartComponent.formatColumnTitle(labelName)}`;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  private calculateChartHeight(): number {
    const viewportHeight = window.innerHeight;
    // Reservado para padding del dialog + header propio (título + close)
    const headerHeight = 90;
    // El control bar (tipo/etiqueta/series/eje) suma alto extra cuando está visible
    const controlsHeight = this.isEditableMode && this.isDialogMode ? 70 : 0;
    const availableHeight = viewportHeight * 0.9 - headerHeight - controlsHeight;

    let rv = Math.max(360, availableHeight);

    return rv;
  }

  private initializeChart(): void {
    try {
      const chartData = this.data?.chartData;
      if (!chartData) {
        _warn('[ChartComponent] initializeChart: chartData ausente');
        return;
      }

      // Multi-serie: delegar al builder dedicado (no aplica el toggle de "registros").
      // Para pie igual usamos single-serie (un pie no representa multi-serie de manera natural).
      if (chartData.multiSeries && chartData.multiSeries.length > 1 && chartData.chartType !== 'pie') {
        try {
          this.initializeMultiSeriesChart();
        } catch (e) {
          _error('[ChartComponent] initializeMultiSeriesChart threw', e);
        }
        return;
      }
      // El bloque siguiente queda como _initializeSingleSerieBody (el código original).
      // Mantengo la indentación original para minimizar el diff.
      this._initializeSingleSerieBody(chartData);
    } catch (e) {
      _error('[ChartComponent] initializeChart unexpected fail', e);
    }
  }

  private _initializeSingleSerieBody(chartData: NonNullable<ChartDialogData['chartData']>): void {
    const { chartType, series } = chartData;

    if (!Array.isArray(series) || series.length === 0) {
      _warn('[ChartComponent] _initializeSingleSerieBody: series vacío o inválido', { chartData });
      return;
    }

    // Store original series data
    this.originalSeries = series;

    // Find the registros column index
    this.registrosColumnIndex = series.findIndex(item => item.label?.includes(this.acummRegisterColumnPartialString));

    // Check if we need to show the toggle
    this.showRegistrosToggle = chartType === 'horizontalBar' && this.registrosColumnIndex !== -1;

    // Log for debugging
    _log('Series data:', series);
    _log('Registros column index:', this.registrosColumnIndex);

    // Configurar altura dinámica
    this.chartConfig.chart = {
      ...this.chartConfig.chart,
      height: this.calculateChartHeight(),
      type: DataBricksAiGenieQueryProcessChartComponent.getApexChartType(chartType),
    };

    // Configurar tipo de gráfico
    this.chartConfig.chart.type = DataBricksAiGenieQueryProcessChartComponent.getApexChartType(chartType);

    // Configurar orientación para barras
    if (chartType === 'horizontalBar') {
      this.chartConfig.plotOptions.bar.horizontal = true;
    }

    // Configurar datos según tipo de gráfico
    if (chartType === 'pie') {
      // Para gráficos tipo pie, los valores van en series y las etiquetas en labels
      this.chartData.series = series.map(item => item.value);
      this.chartConfig.labels = series.map(item => item.label);

      this.chartConfig.chart = {
        ...this.chartConfig.chart,
        type: 'pie' as ApexChartType,
      };

      // Calcular el total y determinar si los valores son porcentajes
      const total = series.reduce((sum, item) => sum + item.value, 0);
      const isPercentageData = Math.abs(total - 100) < 0.1; // Tolerancia de 0.1 para considerar como 100%

      // Configuración específica para pie con porcentajes
      this.chartConfig.plotOptions.pie = {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: {
              show: true,
              formatter: (val: string) => val,
            },
            value: {
              show: true,
              formatter: (val: string) => {
                const numValue = Number(val);
                if (this.isPercentageColumn()) return this.formatNumericValue(numValue);
                if (isPercentageData) return `${numValue.toFixed(1)}%`;

                const percentage = ((numValue / total) * 100).toFixed(1);
                return `${numValue.toLocaleString(this.localStringFormat)} (${percentage}%)`;
              },
            },
          },
        },
      };

      // Configurar tooltip para pie con porcentajes
      this.chartConfig.tooltip = {
        y: {
          formatter: (val: number): string => {
            if (this.isPercentageColumn()) return this.formatNumericValue(val);
            if (isPercentageData) return `${val.toFixed(1)}%`;

            const percentage = ((val / total) * 100).toFixed(1);
            return `${val.toLocaleString(this.localStringFormat)} (${percentage}%)`;
          },
        },
      };

      // Ajustar leyenda para incluir porcentajes
      this.chartConfig.legend = {
        ...this.chartConfig.legend,
        formatter: (seriesName: string, opts: any) => {
          const value = opts.w.globals.series[opts.seriesIndex];
          if (this.isPercentageColumn()) return `${seriesName}: ${this.formatNumericValue(value)}`;
          if (isPercentageData) {
            return `${seriesName}: ${value.toFixed(1)}%`;
          }
          const percentage = ((value / total) * 100).toFixed(1);
          return `${seriesName}: ${value.toLocaleString(this.localStringFormat)} (${percentage}%)`;
        },
      };
    } else if (chartType === 'time_line') {
      const sortedSeries = series;

      this.chartData.series = [
        {
          name: this.data.chartData.numericColumnName,
          data: sortedSeries.map(item => item.value),
        },
      ];

      this.chartConfig.chart = {
        ...this.chartConfig.chart,
        type: 'line',
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
      };

      // Configuración específica para línea temporal
      this.chartConfig.stroke = {
        curve: 'straight',
        width: 3, // Ancho de línea más razonable
      };

      this.chartConfig.markers = {
        size: 5,
        hover: {
          size: 7,
        },
      };

      // Configuración mejorada para el eje X
      this.chartConfig.xaxis = {
        type: 'datetime',
        categories: series.map(item => {
          // Parsear manualmente a UTC para que el gráfico sea agnóstico a la zona horaria local
          const dateMatch = typeof item.label === 'string' && item.label.match(/^(\d{4})-(\d{2})-(\d{2})$/);

          if (dateMatch) {
            const [_, year, month, day] = dateMatch;
            return Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day));
          }

          return new Date(item.label).getTime();
        }),
        labels: {
          formatter: val => _formatChartsDate(val, true),
          style: {
            fontSize: '12px',
          },
        },
        axisBorder: {
          show: true,
        },
        axisTicks: {
          show: true,
        },
      };

      // Configuración mejorada para el eje Y
      this.chartConfig.yaxis = {
        ...this.chartConfig.yaxis,
        labels: {
          formatter: val => this.formatNumericValue(val),
          style: {
            fontSize: '12px',
          },
        },
      };

      // Configuración del tooltip para línea
      this.chartConfig.tooltip = {
        ...this.chartConfig.tooltip,
        y: {
          formatter: val => this.formatNumericValue(val),
        },
      };
    } else {
      // Configuración para gráfico de barras (sin cambios)
      this.updateChartData();
    }

    // Configurar título
    this.chartConfig.title.text = this.getChartTitle();

    // Asegurar formateadores globales después de cualquier override específico por tipo
    this.applyPercentageFormatters();
  }

  /**
   * Renderiza un chart multi-serie (>1 columna numérica vs etiqueta).
   *
   * - Para fechas → líneas con eje X datetime
   * - Para categorías → barras (horizontales si labels largas o muchos registros)
   * - Si `useDualAxis` y exactamente 2 series → eje Y dual (izquierda/derecha)
   */
  private initializeMultiSeriesChart(): void {
    const chartData = this.data?.chartData;
    if (!chartData?.multiSeries?.length) {
      _warn('[ChartComponent] initializeMultiSeriesChart: multiSeries vacío');
      return;
    }

    const { multiSeries, chartType, useDualAxis } = chartData;
    const isTimeLine = chartType === 'time_line';
    const isHorizontal = chartType === 'horizontalBar';
    const apexType: ApexChartType = isTimeLine ? 'line' : 'bar';

    // Toggle "registros (Otros)" no aplica en modo multi-serie
    this.showRegistrosToggle = false;

    // Labels comunes (las series están alineadas por índice).
    const baseLabels = multiSeries[0].data.map(d => d.label);

    // Construir series de Apex
    this.chartData.series = multiSeries.map(s => ({
      name: DataBricksAiGenieQueryProcessChartComponent.formatColumnTitle(s.name),
      data: s.data.map(d => d.value),
    })) as ApexAxisChartSeries;

    // Configuración base
    this.chartConfig.chart = {
      ...this.chartConfig.chart,
      height: this.calculateChartHeight(),
      type: apexType,
      toolbar: isTimeLine
        ? {
            show: true,
            tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
          }
        : this.chartConfig.chart.toolbar,
    };

    if (isHorizontal) {
      this.chartConfig.plotOptions = {
        ...this.chartConfig.plotOptions,
        bar: { ...this.chartConfig.plotOptions.bar, horizontal: true, columnWidth: '70%' },
      };
    }

    if (isTimeLine) {
      this.chartConfig.stroke = { curve: 'straight', width: 3 };
      this.chartConfig.markers = { size: 4, hover: { size: 6 } };

      this.chartConfig.xaxis = {
        type: 'datetime',
        categories: baseLabels.map(label => {
          const dateMatch = typeof label === 'string' && label.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (dateMatch) {
            const [_, year, month, day] = dateMatch;
            return Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day));
          }
          return new Date(label).getTime();
        }),
        labels: {
          formatter: val => _formatChartsDate(val, true),
          style: { fontSize: '12px' },
        },
      };
    } else {
      this.chartConfig.xaxis = {
        type: 'category',
        categories: baseLabels,
        labels: { rotate: -45, trim: true, maxHeight: 100 },
      };
    }

    // Eje Y: dual o único.
    // Apex no soporta múltiples Y axis con barras horizontales — forzamos shared en ese caso
    // para evitar el error "Multiple Y Axis for bars are not supported".
    const effectiveDualAxis = useDualAxis && multiSeries.length === 2 && !isHorizontal;
    if (effectiveDualAxis) {
      const yAxes: ApexYAxis[] = multiSeries.map((s, idx) => {
        const seriesName = DataBricksAiGenieQueryProcessChartComponent.formatColumnTitle(s.name);
        const color = this.chartConfig.colors[idx % this.chartConfig.colors.length];
        return {
          seriesName,
          opposite: idx === 1,
          axisTicks: { show: true },
          axisBorder: { show: true, color },
          labels: {
            style: { colors: color },
            formatter: (val: number) => this.formatNumericValueForSeries(val, s.name),
          },
          title: { text: seriesName, style: { color } },
        } as ApexYAxis;
      });
      this.chartConfig.yaxis = yAxes as unknown as ApexYAxis;
    } else if (isHorizontal) {
      // Horizontal: las CATEGORÍAS (strings, ej fechas) se renderizan en el eje Y; los valores numéricos en el X.
      // No aplicar formatter numérico al yaxis — `Number("2024-01-01")` da NaN y se mostraba "NaN" en cada barra.
      this.chartConfig.yaxis = {
        labels: { style: { fontSize: '12px' } },
      };
      this.chartConfig.xaxis = {
        ...this.chartConfig.xaxis,
        labels: {
          ...(this.chartConfig.xaxis?.labels || {}),
          formatter: (val: any) => this.formatNumericValue(Number(val)),
        },
      };
    } else {
      this.chartConfig.yaxis = {
        labels: { formatter: (val: number) => this.formatNumericValue(val), style: { fontSize: '12px' } },
      };
    }

    // Tooltip: usa formato según el nombre de la serie para detectar porcentajes por serie
    this.chartConfig.tooltip = {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number, opts: any) => {
          const seriesName = multiSeries[opts?.seriesIndex]?.name;
          return seriesName ? this.formatNumericValueForSeries(val, seriesName) : this.formatNumericValue(val);
        },
      },
    } as ApexTooltip;

    this.chartConfig.legend = { ...this.chartConfig.legend, show: true, position: 'top' };
    this.chartConfig.title.text = this.getChartTitle();

    _log('[initializeMultiSeriesChart]', {
      seriesCount: multiSeries.length,
      labelsCount: baseLabels.length,
      useDualAxis: effectiveDualAxis,
      chartType,
    });
  }

  /** Formateo por serie: detecta porcentaje a partir del nombre de columna (no del global). */
  private formatNumericValueForSeries(val: number, columnName: string): string {
    if (val === null || val === undefined) return '';
    const name = (columnName || '').toLowerCase();
    const isPct =
      name.includes('pct') ||
      name.includes('porcent') ||
      name.includes('%') ||
      name.includes('variacion') ||
      name.includes('variación') ||
      name.includes('ratio') ||
      name.includes('growth');

    const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: isPct ? 1 : 0,
      maximumFractionDigits: 2,
    };
    const formatted = Number(val).toLocaleString(this.localStringFormat, options);
    return isPct ? `${formatted}%` : formatted;
  }

  private applyPercentageFormatters(): void {
    const isHorizontal = this.chartConfig.plotOptions?.bar?.horizontal === true;

    // Si es horizontal, los valores numéricos están en el eje X
    if (isHorizontal) {
      this.chartConfig.xaxis = {
        ...(this.chartConfig.xaxis || {}),
        labels: {
          ...(this.chartConfig.xaxis?.labels || {}),
          formatter: (val: any) => this.formatNumericValue(Number(val)),
        },
      };
      // En horizontal el eje Y muestra CATEGORÍAS (strings, ej fechas) → quitar formatter numérico
      // para evitar que "2024-01-01" → Number → NaN se renderice como "NaN".
      this.chartConfig.yaxis = {
        ...(this.chartConfig.yaxis || {}),
        labels: {
          ...(this.chartConfig.yaxis?.labels || {}),
          formatter: undefined,
        },
      };
    } else {
      // Si es vertical (barras o líneas), los valores numéricos están en el eje Y
      this.chartConfig.yaxis = {
        ...(this.chartConfig.yaxis || {}),
        labels: {
          ...(this.chartConfig.yaxis?.labels || {}),
          formatter: (val: any) => this.formatNumericValue(Number(val)),
        },
      };
    }

    // El tooltip siempre debe tener el formato correcto
    this.chartConfig.tooltip = {
      ...(this.chartConfig.tooltip || {}),
      y: {
        ...(this.chartConfig.tooltip?.y || {}),
        formatter: (val: any) => this.formatNumericValue(Number(val)),
      },
    };
  }

  onToggleChange(): void {
    this.updateChartData();
    this.cdRef.detectChanges();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Handlers de los controles in-chart
  // ──────────────────────────────────────────────────────────────────────────

  onChartTypeChange(): void {
    try {
      // Si el nuevo tipo no soporta multi-serie, recortar a una sola.
      if (!this.isMultiSeriesAllowed && this.selectedSeries.length > 1) {
        this.selectedSeries = [this.selectedSeries[0]];
      }
      this.rebuildChartFromControls();
    } catch (e) {
      _error('[ChartComponent] onChartTypeChange fail', e);
    }
  }

  onLabelChange(): void {
    try {
      this.rebuildChartFromControls();
    } catch (e) {
      _error('[ChartComponent] onLabelChange fail', e);
    }
  }

  onSeriesChange(): void {
    try {
      // mat-select multi-mode puede devolver string (raro pero defensivo)
      if (!Array.isArray(this.selectedSeries)) {
        this.selectedSeries = this.selectedSeries ? [this.selectedSeries as unknown as string] : [];
      }
      // Si el tipo no admite multi pero quedaron varias seleccionadas, recortar.
      if (!this.isMultiSeriesAllowed && this.selectedSeries.length > 1) {
        this.selectedSeries = [this.selectedSeries[0]];
      }
      this.rebuildChartFromControls();
    } catch (e) {
      _error('[ChartComponent] onSeriesChange fail', e);
    }
  }

  onAxisModeChange(): void {
    try {
      this.rebuildChartFromControls();
    } catch (e) {
      _error('[ChartComponent] onAxisModeChange fail', e);
    }
  }

  /**
   * Re-arma `chartMetadata` ejecutando el analyzer correspondiente con los valores actuales
   * de los controles, y dispara una re-render del chart. Si no hay queryResult disponible,
   * no hace nada (la UI de edición ya estaría oculta por `isEditableMode=false`).
   */
  private rebuildChartFromControls(): void {
    try {
      const qr = this.data?.queryProcessResult;
      if (!qr || !this.selectedLabelColumn || !this.selectedSeries?.length) {
        _warn('[rebuildChartFromControls] datos insuficientes para reconstruir', {
          hasQr: !!qr,
          label: this.selectedLabelColumn,
          series: this.selectedSeries,
        });
        return;
      }

      // Defensa: si el label coincide con alguna de las series, removerla de las series
      // (el analyzer puede comportarse raro con la misma columna en label y serie).
      const seriesNoLabel = this.selectedSeries.filter(s => s && s !== this.selectedLabelColumn);
      if (seriesNoLabel.length === 0) {
        _warn('[rebuildChartFromControls] label y serie son la misma columna — selección inválida');
        return;
      }
      this.selectedSeries = seriesNoLabel;

      const isMulti = this.selectedSeries.length > 1 && this.isMultiSeriesAllowed;

      let analysis: DataBricksAiGenie_ChartAnalysisResult | undefined;
      try {
        if (isMulti) {
          analysis = DataBricksAiGenie_AnalyzeMultiSeriesChartData(qr, this.selectedSeries, this.selectedLabelColumn);
        } else {
          const filtered = this.buildSingleSerieFilteredResult(qr, this.selectedSeries[0], this.selectedLabelColumn);
          if (!filtered) {
            _warn('[rebuildChartFromControls] no se pudo construir filtered result');
            return;
          }
          analysis = DataBricksAiGenie_AnalyzeSimpleChartData(filtered);
        }
      } catch (analyzerErr) {
        _error('[rebuildChartFromControls] analyzer threw', analyzerErr);
        return;
      }

      if (!analysis?.isVisualizationAvailable || !analysis.visualization) {
        _warn('[rebuildChartFromControls] analyzer no devolvió visualización', analysis);
        return;
      }

      // Forzar el chartType seleccionado por el usuario (el analyzer puede sugerir otro).
      analysis.visualization.chartType = this.selectedChartType;

      // Aplicar override de modo de eje cuando es multi
      if (isMulti) {
        if (this.axisMode === 'dual') {
          analysis.visualization.useDualAxis = true;
        } else if (this.axisMode === 'shared') {
          analysis.visualization.useDualAxis = false;
        }
        // 'auto' deja lo que decidió el analyzer
      }

      // Reemplazar el chartData del data dialog y re-inicializar
      this.data.chartData = analysis.visualization;
      this.chartMetadata = analysis;

      // Reset de estado interno del chart antes de re-inicializar
      this.chartConfig = _cloneDeep(chartConfig);
      this.chartData = { series: [] };
      this.showRegistrosToggle = false;
      this.originalSeries = [];
      this.registrosColumnIndex = -1;
      this.isUpdating = false;

      setTimeout(() => {
        if (this.destroyed) return;
        try {
          this.initializeChart();
        } catch (initErr) {
          _error('[rebuildChartFromControls] initializeChart threw', initErr);
        }
        if (!this.destroyed) this.cdRef.detectChanges();
      }, 16);
    } catch (e) {
      _error('[rebuildChartFromControls] unexpected fail', e);
    }
  }

  /** Construye un GenieStatementResponse recortado a 1 numérica + 1 label (para reuse del analyzer simple). */
  private buildSingleSerieFilteredResult(qr: GenieStatementResponse, numericCol: string, labelCol: string): GenieStatementResponse | null {
    try {
      const stmt = qr?.statement_response;
      if (!stmt?.manifest?.schema?.columns || !stmt?.result?.data_typed_array) {
        _warn('[buildSingleSerieFilteredResult] queryResult inválido', { qr });
        return null;
      }

      const original = _cloneDeep(stmt);
      const numIdx = original.manifest.schema.columns.findIndex((c: any) => c.name === numericCol);
      const labelIdx = original.manifest.schema.columns.findIndex((c: any) => c.name === labelCol);

      if (numIdx === -1 || labelIdx === -1) {
        _warn('[buildSingleSerieFilteredResult] columna no encontrada', { numericCol, labelCol, numIdx, labelIdx });
        return null;
      }

      const cols = [original.manifest.schema.columns[labelIdx], original.manifest.schema.columns[numIdx]];
      const newSchema = { ...original.manifest.schema, column_count: 2, columns: cols };
      const newData = (original.result.data_typed_array || []).map((row: any) => ({
        values: [row?.values?.[labelIdx], row?.values?.[numIdx]],
      }));

      return {
        ...qr,
        statement_response: {
          ...original,
          manifest: { ...original.manifest, schema: newSchema },
          result: { ...original.result, data_typed_array: newData },
        },
      };
    } catch (e) {
      _error('[buildSingleSerieFilteredResult] threw', e);
      return null;
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // i18n helpers para los controles
  // ──────────────────────────────────────────────────────────────────────────

  getTypeLabel(): string {
    return this.translate.instant('DATA_VISUALIZATION.CHART.TYPE') || 'Tipo';
  }

  getLabelAxisLabel(): string {
    return this.translate.instant('DATA_VISUALIZATION.CHART.LABEL_COLUMN') || 'Etiqueta';
  }

  getSeriesLabel(): string {
    return this.translate.instant('DATA_VISUALIZATION.CHART.SERIES') || 'Series';
  }

  getAxisModeLabel(): string {
    return this.translate.instant('DATA_VISUALIZATION.CHART.AXIS_MODE') || 'Ejes';
  }

  getTypeOptionLabel(t: SelectableChartType): string {
    const key = `DATA_VISUALIZATION.CHART.TYPE_${t.toUpperCase()}`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) return translated;
    switch (t) {
      case 'bar':
        return 'Barras';
      case 'horizontalBar':
        return 'Barras horizontales';
      case 'time_line':
        return 'Líneas';
      case 'pie':
        return 'Torta';
      default:
        return t;
    }
  }

  getAxisModeOptionLabel(mode: AxisMode): string {
    const key = `DATA_VISUALIZATION.CHART.AXIS_${mode.toUpperCase()}`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) return translated;
    switch (mode) {
      case 'auto':
        return 'Auto';
      case 'shared':
        return 'Eje único';
      case 'dual':
        return 'Eje dual';
    }
  }

  formatColumnTitle(col: string): string {
    return DataBricksAiGenieQueryProcessChartComponent.formatColumnTitle(col);
  }

  private updateChartData(): void {
    this.isUpdating = true;
    this.cdRef.detectChanges();

    setTimeout(() => {
      let filteredSeries = _cloneDeep(this.originalSeries);

      if (this.registrosColumnIndex !== -1 && !this.showRegistrosData) {
        filteredSeries.splice(this.registrosColumnIndex, 1);
      }

      const newSeries = [
        {
          name: this.data.chartData.numericColumnName,
          data: filteredSeries.map(item => item.value),
        },
      ];

      const newCategories = filteredSeries.map(item => {
        const label = item.label;

        if (DataBricksAiGenieQueryProcessChartComponent.isDateLabel(label)) {
          return DataBricksAiGenieQueryProcessChartComponent.formatChartDate(label);
        }

        return label;
      });

      // Crear nueva configuración
      this.chartConfig = {
        ..._cloneDeep(chartConfig),
        chart: {
          ...chartConfig.chart,
          type: 'bar',
          height: this.calculateChartHeight(),
        },
        plotOptions: {
          ...chartConfig.plotOptions,
          bar: {
            horizontal: true,
            columnWidth: '70%',
          },
        },
        xaxis: {
          type: 'category',
          categories: newCategories,
          labels: {
            rotate: -45,
            trim: true,
            maxHeight: 100,
          },
        },
        yaxis: {
          ...chartConfig.yaxis,
          labels: {
            formatter: (val: number) => this.formatNumericValue(val),
          },
        },
        tooltip: {
          ...chartConfig.tooltip,
          y: {
            formatter: (val: number) => this.formatNumericValue(val),
          },
        },
        title: {
          ...chartConfig.title,
          text: this.getChartTitle(),
        },
      };

      this.chartData = {
        series: newSeries,
      };

      this.applyPercentageFormatters();

      _log('Chart update with:', {
        filteredLength: filteredSeries.length,
        originalLength: this.originalSeries.length,
        categories: newCategories,
        series: newSeries,
      });

      this.isUpdating = false;
      this.cdRef.detectChanges();
    }, 100);
  }

  static getApexChartType(type: string): ApexChartType {
    switch (type) {
      case 'pie':
        return 'pie';
      case 'time_line':
      case 'line':
        return 'line';
      case 'bar':
      case 'horizontalBar':
      case 'horizontal':
        return 'bar';
      default:
        return 'bar';
    }
  }

  static formatColumnTitle(columnName: string): string {
    if (!columnName) return '';
    return (
      columnName
        // Separar por underscore y otros caracteres especiales
        .split(/[_\s]+/)
        // Capitalizar primera letra de cada palabra
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        // Unir con espacios
        .join(' ')
    );
  }

  static isDateLabel(label: any): boolean {
    if (!label) return false;
    const dateRegex = /^(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}|\d{2}-\d{2}-\d{4})/;
    return typeof label === 'string' && (dateRegex.test(label) || !isNaN(Date.parse(label)));
  }

  static formatChartDate(label: any): string {
    return _formatChartsDate(label, true);
  }

  // Translation methods
  getShowGroupedValuesText(): string {
    return this.translate.instant('DATA_VISUALIZATION.CHART.SHOW_GROUPED_VALUES_OTHERS') || 'Mostrar valores agrupados (Otros)';
  }

  getShowGroupedValuesTooltip(): string {
    return this.translate.instant('DATA_VISUALIZATION.CHART.SHOW_GROUPED_VALUES') || 'Mostrar valores agrupados';
  }

  private isPercentageColumn(): boolean {
    const name = (this.data.chartData.numericColumnName || '').toLowerCase();

    const title = this.getChartTitle().toLowerCase();

    return (
      name.includes('pct') ||
      name.includes('porcent') ||
      name.includes('%') ||
      name.includes('variacion') ||
      name.includes('variación') ||
      name.includes('ratio') ||
      name.includes('growth') ||
      title.includes('pct') ||
      title.includes('porcent') ||
      title.includes('%')
    );
  }

  private formatNumericValue(val: number): string {
    if (val === null || val === undefined) return '';

    const isPct = this.isPercentageColumn();

    // Si es un porcentaje, queremos mostrar decimales siempre para precisión
    const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: isPct ? 1 : 0,
      maximumFractionDigits: 2,
    };

    const formatted = Number(val).toLocaleString(this.localStringFormat, options);

    return isPct ? `${formatted}%` : formatted;
  }
}
